import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"
import { COLUMN_ORDER, findOption, HINTS } from "comm_chart_data"
import { preserveScroll } from "controllers/scroll_preserve"
import { renderIcons } from "lucide_icons"

// Communication-chart controller — the left/editor side of the cascading
// picker UX. Renders the rows-by-4-columns grid for
// plan.about.communicationChart and hands off the actual option picking
// to the wizard pane via custom events.
//
// Flow:
//   1. User clicks a cell → controller marks it active and dispatches
//      "comm-chart:cell-select" with { rowId, col, state }.
//   2. The wizard pane picks up the event, renders the cascading
//      option picker (situations → behaviors → meanings → supports),
//      and emits "comm-chart:cell-fill" with { rowId, col, value, icon }.
//   3. This controller catches that fill event, writes the value to
//      plan.about.communicationChart, saves, and re-renders the row.
//   4. After a non-final column is filled the focus advances to the
//      next column on the same row so the user can keep chaining
//      without leaving the keyboard / mouse on the chart.

const STORAGE_KEY = "pcp_plan_data"
const PATH        = "about.communicationChart"

// Whole-word "TBD" (case-insensitive) — mirrors dynamic_list_controller
// TBD_RX so BCC cells flag the same way as list rows and the legacy
// editor (pcp_builder.html#hasTBD).
const TBD_RX      = /\bTBD\b/i

const PLACEHOLDER = {
  happening: "Pick a situation…",
  behavior:  "Pick a behavior…",
  meaning:   "Pick a meaning…",
  support:   "Pick a support…"
}

// Top-5 "What's happening" scenarios from the legacy editor's wizard —
// these long-form, plain-language phrases are the planner-friendly
// starters used in IPP sessions. They DO NOT match the SITUATIONS
// dropdown values exactly — the wizard treats them as free-text seeds
// and uses context-aware HINTS (below) for the other columns.
const SEED_HAPPENINGS = [
  "Hungry or thirsty",
  "Tired or needs rest",
  "In pain or not feeling well",
  "Frustrated or upset",
  "Overwhelmed by noise or crowds"
]

// Context-aware placeholders (HINTS) now live in comm_chart_data.js so
// the editor chart cells and the wizard picker's "Suggested" option share
// one source of truth. Imported above.

export default class extends Controller {
  static targets = ["list"]

  connect() {
    this.plan = this._load()
    this._ensureArray()
    this._seedIfEmpty()
    this._activeRowId = null
    this._activeCol   = null
    this.render()

    this._onPlanChange = (e) => {
      if (this._suppressNextChange) return
      const p = e.detail && e.detail.path
      if (!p) return
      if (p === PATH || p.startsWith(`${PATH}.`)) {
        this.plan = e.detail.plan || this._load()
        this._ensureArray()
        this.render()
      }
    }
    document.addEventListener("plan:change", this._onPlanChange)

    this._onCellFill = (e) => {
      const d = e.detail || {}
      if (d.rowId == null || !d.col) return
      this._applyPick(d.rowId, d.col, d.value, d.icon)
    }
    document.addEventListener("comm-chart:cell-fill", this._onCellFill)

    this._onClearActive = () => this._clearActiveCell()
    document.addEventListener("comm-chart:clear-active", this._onClearActive)

    // Esc deselects whichever cell is highlighted so the wizard pane
    // can return to its "click any cell" empty state.
    this._onKey = (e) => {
      if (e.key === "Escape" && this._activeRowId != null) {
        this._clearActiveCell()
        document.dispatchEvent(new CustomEvent("comm-chart:cell-deselect", { bubbles: true }))
      }
    }
    document.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onPlanChange)
    document.removeEventListener("comm-chart:cell-fill", this._onCellFill)
    document.removeEventListener("comm-chart:clear-active", this._onClearActive)
    document.removeEventListener("keydown", this._onKey)
  }

  // ---- actions ----

  addRow() {
    const rows = this._getArr()
    rows.push({ id: this._nextId(), happening: "", behavior: "", meaning: "", support: "" })
    this._setArr(rows)
    this._save()
    this.render()
    this._dispatchChange()
  }

  removeRow(event) {
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const id = this._idOf(row)
    if (this._activeRowId === id) {
      this._activeRowId = null
      this._activeCol   = null
      document.dispatchEvent(new CustomEvent("comm-chart:cell-deselect", { bubbles: true }))
    }
    this._setArr(this._getArr().filter(r => r.id !== id))
    this._save()
    this.render()
    this._dispatchChange()
  }

  // ---- Drag-reorder ----
  // Mirrors dynamic-list / revision-history so the chart rows reorder
  // with the same grip handle + drop-edge cue as the other sections.
  dragStart(event) {
    const row = event.currentTarget
    this._dragFromId = this._idOf(row)
    row.classList.add("cos-row-dragging")
    event.dataTransfer.effectAllowed = "move"
    try { event.dataTransfer.setData("text/plain", String(this._dragFromId)) } catch (_) {}
  }

  dragOver(event) {
    if (this._dragFromId == null) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    const row = event.currentTarget
    if (this._idOf(row) === this._dragFromId) return
    const rect = row.getBoundingClientRect()
    const before = (event.clientY - rect.top) < rect.height / 2
    this.listTarget.querySelectorAll(".cos-row-drop-before, .cos-row-drop-after")
      .forEach(r => r.classList.remove("cos-row-drop-before", "cos-row-drop-after"))
    row.classList.add(before ? "cos-row-drop-before" : "cos-row-drop-after")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("cos-row-drop-before", "cos-row-drop-after")
  }

  drop(event) {
    if (this._dragFromId == null) return
    event.preventDefault()
    const row = event.currentTarget
    const fromId = this._dragFromId
    const toId   = this._idOf(row)
    this._clearDragMarkers()
    if (fromId === toId) return

    const items = this._getArr()
    const fromIdx = items.findIndex(r => r.id === fromId)
    let   toIdx   = items.findIndex(r => r.id === toId)
    if (fromIdx < 0 || toIdx < 0) return

    const rect = row.getBoundingClientRect()
    const before = (event.clientY - rect.top) < rect.height / 2
    let insertAt = before ? toIdx : toIdx + 1
    if (fromIdx < insertAt) insertAt -= 1

    const [moved] = items.splice(fromIdx, 1)
    items.splice(insertAt, 0, moved)
    this._setArr(items)
    this._save()
    this.render()
    this._dispatchChange()
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("cos-row-dragging")
    this._clearDragMarkers()
    this._dragFromId = null
  }

  _clearDragMarkers() {
    this.listTarget.querySelectorAll(".cos-row-drop-before, .cos-row-drop-after, .cos-row-dragging")
      .forEach(r => r.classList.remove("cos-row-drop-before", "cos-row-drop-after", "cos-row-dragging"))
  }

  selectCell(event) {
    const cell = event.currentTarget
    const row  = cell.closest("[data-row]")
    if (!row) return
    const rowId = this._idOf(row)
    const col   = cell.dataset.col
    if (!col) return
    this._setActiveCell(rowId, col)
    const item = this._getArr().find(r => r.id === rowId) || {}
    document.dispatchEvent(new CustomEvent("comm-chart:cell-select", {
      bubbles: true,
      detail: {
        rowId,
        col,
        state: {
          happening: item.happening || "",
          behavior:  item.behavior  || "",
          meaning:   item.meaning   || "",
          support:   item.support   || ""
        }
      }
    }))
  }

  // Free-text edits from the cell textarea. Writes through to the same
  // plan field the wizard picker fills, then auto-grows the textarea
  // so wrapped sentences stay visible. No cascade clearing — that's
  // reserved for picker-driven changes (see _applyPick).
  updateCell(event) {
    const ta = event.currentTarget
    const row = ta.closest("[data-row]")
    if (!row) return
    const rowId = this._idOf(row)
    const col   = ta.dataset.col
    if (!col) return
    const rows = this._getArr()
    const item = rows.find(r => r.id === rowId)
    if (!item) return
    item[col] = ta.value
    this._setArr(rows)
    this._save()
    this._dispatchChange()
    this._autoSize(ta)
    this._refreshRowTbd(row)
  }

  // Toggle amber TBD styling on a BCC row + its cells when any cell
  // still contains the word "TBD". Parity with the legacy editor and
  // dynamic_list_controller._refreshRowTbd so "TBD" flags the same way
  // everywhere. No badge here — the row is a fixed 6-column grid, so we
  // rely on the .cc-tbd-row inset bar + .cc-tbd-field cell tint instead.
  _refreshRowTbd(row) {
    if (!row) return
    let rowHas = false
    row.querySelectorAll(".cc-cell-input").forEach(f => {
      const isTbd = TBD_RX.test(f.value || "")
      f.classList.toggle("cc-tbd-field", isTbd)
      if (isTbd) rowHas = true
    })
    row.classList.toggle("cc-tbd-row", rowHas)
  }

  // Border-aware scrollHeight math (mirrors dynamic-list / revision-history).
  // Floors at 38px so an empty cell still feels tappable. ResizeObserver
  // re-runs when the cell's width changes (split-pane resize, sidebar
  // collapse) so wrapped content always fits.
  _autoSize(el) {
    if (!el || el.tagName !== "TEXTAREA") return
    const prev = el.style.transition
    el.style.transition = "none"
    el.style.height = "auto"
    const borderY = (el.offsetHeight - el.clientHeight) || 2
    el.style.height = `${Math.max(el.scrollHeight + borderY, 38)}px`
    void el.offsetHeight
    el.style.transition = prev
    if (!el._ccSizer && typeof ResizeObserver !== "undefined") {
      let lastWidth = el.offsetWidth
      el._ccSizer = new ResizeObserver(() => {
        const w = el.offsetWidth
        if (w === lastWidth) return
        lastWidth = w
        this._autoSize(el)
      })
      el._ccSizer.observe(el)
    }
  }

  // ---- rendering ----

  render() {
    preserveScroll(this.element, () => {
      const rows = this._getArr()
      const html = rows.map((r, idx) => this._rowHtml(r, idx)).join("")
      this.listTarget.innerHTML = html
      this._paintActive()
      // Auto-size each cell textarea now that the DOM is in place —
      // scrollHeight measurements only work after the element is mounted.
      // Otherwise saved long sentences would render at the default 38px
      // single-line height until the user clicked into them.
      this.listTarget.querySelectorAll(".cc-cell-input").forEach(t => this._autoSize(t))
      this.listTarget.querySelectorAll("[data-row]").forEach(r => this._refreshRowTbd(r))
      renderIcons(this.element)
    })
  }

  _rowHtml(row, idx) {
    const cells = COLUMN_ORDER.map(col => this._cellHtml(row, col)).join("")
    return `
      <div class="cc-row cos-row-draggable"
           data-row
           draggable="true"
           data-row-id="${row.id}"
           data-action="dragstart->comm-chart#dragStart dragover->comm-chart#dragOver drop->comm-chart#drop dragend->comm-chart#dragEnd dragleave->comm-chart#dragLeave">
        <div class="cc-cell-num cc-cell-drag">
          <span class="cos-drag-handle" title="Drag to reorder" aria-label="Drag to reorder">
            <i data-lucide="grip-vertical"></i>
          </span>
          <span class="cc-row-idx">${idx + 1}</span>
        </div>
        ${cells}
        <button type="button"
                class="cc-cell-del"
                data-action="comm-chart#removeRow"
                title="Remove row">
          <i data-lucide="trash-2"></i>
        </button>
      </div>`
  }

  _cellHtml(row, col) {
    const val = row[col] || ""
    // Context-aware placeholder: when the row's "What's happening" is
    // one of the seeded scenarios with a HINTS entry, surface the
    // matching `e.g.` hint per column. Falls back to the generic
    // "Pick a ___" PLACEHOLDER otherwise.
    const hint = (col !== "happening" && HINTS[row.happening] && HINTS[row.happening][col]) || null
    const placeholder = hint || PLACEHOLDER[col]
    return `
      <div class="cc-cell" data-col="${col}">
        <textarea class="cc-cell-input"
                  data-col="${col}"
                  data-action="input->comm-chart#updateCell focus->comm-chart#selectCell"
                  rows="1"
                  placeholder="${this._escAttr(placeholder)}">${this._escHtml(val)}</textarea>
      </div>`
  }

  // ---- state plumbing ----

  _applyPick(rowId, col, value, icon) {
    const rows = this._getArr()
    const item = rows.find(r => r.id === rowId)
    if (!item) return
    item[col] = value || ""
    // Strict cascade: clearing a parent invalidates downstream picks
    // unless the user is explicitly editing (keep them on Enter custom).
    if (value) {
      const idx = COLUMN_ORDER.indexOf(col)
      for (let i = idx + 1; i < COLUMN_ORDER.length; i++) {
        const downCol = COLUMN_ORDER[i]
        const downOpt = findOption(downCol, {
          happening: item.happening, behavior: item.behavior,
          meaning:   item.meaning,   support:  item.support
        }, item[downCol])
        // If the previously-picked downstream value isn't valid under
        // the new parent context (no override hit, no generic hit),
        // clear it so the user can refill.
        if (item[downCol] && !downOpt) item[downCol] = ""
      }
    }
    this._setArr(rows)
    this._save()
    this.render()
    this._dispatchChange()

    // Advance to next column (if any) so the user keeps the picker
    // open and can chain clicks without re-targeting the chart.
    const nextIdx = COLUMN_ORDER.indexOf(col) + 1
    if (nextIdx < COLUMN_ORDER.length) {
      const nextCol = COLUMN_ORDER[nextIdx]
      this._setActiveCell(rowId, nextCol)
      const refreshed = this._getArr().find(r => r.id === rowId) || {}
      document.dispatchEvent(new CustomEvent("comm-chart:cell-select", {
        bubbles: true,
        detail: {
          rowId, col: nextCol,
          state: {
            happening: refreshed.happening || "",
            behavior:  refreshed.behavior  || "",
            meaning:   refreshed.meaning   || "",
            support:   refreshed.support   || ""
          }
        }
      }))
    } else {
      this._clearActiveCell()
      document.dispatchEvent(new CustomEvent("comm-chart:row-complete", {
        bubbles: true,
        detail: { rowId }
      }))
    }
  }

  _setActiveCell(rowId, col) {
    this._activeRowId = rowId
    this._activeCol   = col
    this._paintActive()
  }

  _clearActiveCell() {
    this._activeRowId = null
    this._activeCol   = null
    this._paintActive()
  }

  _paintActive() {
    this.listTarget.querySelectorAll(".cc-cell.is-active").forEach(c => c.classList.remove("is-active"))
    if (this._activeRowId == null) return
    const row = this.listTarget.querySelector(`[data-row-id="${this._activeRowId}"]`)
    if (!row) return
    const cell = row.querySelector(`.cc-cell[data-col="${this._activeCol}"]`)
    if (cell) cell.classList.add("is-active")
  }

  // ---- storage helpers ----

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
  _save() {
    savePlan(this.plan)
  }
  _ensureArray() {
    if (!this.plan.about || typeof this.plan.about !== "object") this.plan.about = {}
    if (!Array.isArray(this.plan.about.communicationChart)) this.plan.about.communicationChart = []
    // Strip the builder's default example row if it leaked in.
    const isBuilderExample = (r) =>
      r && r.happening === "I am hungry"
        && r.behavior === "I pace around the kitchen"
        && r.meaning === "I want a snack"
        && r.support === "Offer me a choice of healthy snacks"
    this.plan.about.communicationChart = this.plan.about.communicationChart.filter(r => !isBuilderExample(r))
    // Backfill missing ids so cells can find their owning row.
    this.plan.about.communicationChart.forEach(r => {
      if (r.id == null) r.id = this._nextId()
      if (typeof r.happening !== "string") r.happening = ""
      if (typeof r.behavior  !== "string") r.behavior  = ""
      if (typeof r.meaning   !== "string") r.meaning   = ""
      if (typeof r.support   !== "string") r.support   = ""
    })
  }
  _seedIfEmpty() {
    if (!this.plan.meta || typeof this.plan.meta !== "object") this.plan.meta = {}
    const rows = this._getArr()
    if (this.plan.meta.commChartSeeded || rows.length > 0) return
    SEED_HAPPENINGS.forEach(h => rows.push({
      id: this._nextId(),
      happening: h, behavior: "", meaning: "", support: ""
    }))
    this.plan.meta.commChartSeeded = true
    this._setArr(rows)
    this._save()
  }
  _getArr() {
    return (this.plan.about && this.plan.about.communicationChart) || []
  }
  _setArr(arr) {
    this.plan.about.communicationChart = arr
  }
  _nextId() {
    return Date.now() + Math.random()
  }
  _idOf(row) {
    const raw = row.dataset.rowId
    const n = parseFloat(raw)
    return Number.isFinite(n) ? n : raw
  }
  _dispatchChange() {
    this._suppressNextChange = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: PATH, plan: this.plan }
    }))
    this._suppressNextChange = false
  }

  _escHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, c => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }
  _escAttr(s) { return this._escHtml(s) }
}
